/* ==========================================================================
   Admin Per-Provider Settings Page (PORTAL-72)
   Configure setting overrides for a single provider. Each setting either
   inherits the pharmacy default (Portal Configuration) or is explicitly
   overridden on/off for this provider.
   ========================================================================== */

import { useState, useEffect, useCallback, useRef, type KeyboardEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { useAdminAuth } from '@/hooks/useAdminAuth'
import { useToast } from '@/context/ToastContext'
import { getProvider, getProviderSettings, updateProviderSettings } from '@/api/admin'
import { ApiClientError } from '@/api/client'
import type {
  ProviderResponse,
  ProviderSettingsUpdate,
  ProviderSettingsValues,
} from '@/api/types'
import {
  PAYMENT_DELIVERY_CHILD_KEYS,
  PAYMENT_DELIVERY_REQUIRED_SETTINGS,
  PAYMENT_DELIVERY_SETTINGS,
  REQUIRED_FIELD_HIDE_KEY,
  generalSettingsFor,
  overridesFromResponse,
  type OverrideState,
  type SettingKey,
  type SettingMeta,
} from '@/utils/providerSettings'

/* --------------------------------------------------------------------------
   Tri-state segmented control
   -------------------------------------------------------------------------- */

interface SettingRowProps {
  meta: SettingMeta
  value: boolean | null
  defaultValue: boolean
  /** Resolved effective value for this provider — reflects the parent→child
   *  cascade the backend applies. Read-only; updates on each change. */
  effectiveValue: boolean
  onChange: (value: boolean | null) => void
  disabled?: boolean
  /** When set, the control is locked and this note explains why (e.g. a child
   *  field forced hidden while the Payment & Delivery section is hidden). */
  lockedNote?: string
  /** Renders a compact, indented sub-row with this label and no description
   *  (PORTAL-202 requirements under their field's Hide row). The radio group
   *  keeps `meta.label` as its accessible name. */
  compactLabel?: string
}

function SettingRow({
  meta,
  value,
  defaultValue,
  effectiveValue,
  onChange,
  disabled,
  lockedNote,
  compactLabel,
}: SettingRowProps) {
  const options: { label: string; val: boolean | null }[] = [
    { label: `Default (${defaultValue ? 'On' : 'Off'})`, val: null },
    { label: 'On', val: true },
    { label: 'Off', val: false },
  ]

  const groupRef = useRef<HTMLDivElement>(null)

  // Arrow-key navigation for the radio group (WAI-ARIA radio pattern): Left/Up
  // and Right/Down move focus AND selection to the adjacent option, wrapping at
  // the ends. With the roving tabIndex below (only the checked option is
  // tabbable), this makes the group a single tab stop that behaves like a native
  // radio group instead of three independently-tabbable buttons.
  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return
    const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown'
    const back = e.key === 'ArrowLeft' || e.key === 'ArrowUp'
    if (!forward && !back) return
    e.preventDefault()
    const current = options.findIndex((o) => o.val === value)
    const next = (current + (forward ? 1 : -1) + options.length) % options.length
    onChange(options[next].val)
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>(
      'button[role="radio"]'
    )
    buttons?.[next]?.focus()
  }

  return (
    <div
      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between ${
        compactLabel ? 'gap-2 pb-4 pl-4' : 'gap-3 py-4'
      }`}
    >
      {compactLabel ? (
        <div className="sm:pr-6">
          <p className="text-xs font-medium text-charcoal">{compactLabel}</p>
          <p className="mt-0.5 text-xs text-charcoal/60">
            In effect: {effectiveValue ? 'On' : 'Off'}
          </p>
          {lockedNote && (
            <p className="mt-0.5 text-xs italic text-gray-400">{lockedNote}</p>
          )}
        </div>
      ) : (
        <div className="sm:pr-6">
          <p className="text-sm font-medium text-charcoal">{meta.label}</p>
          <p className="mt-0.5 text-xs text-gray-500">{meta.description}</p>
          <p className="mt-1 text-xs font-medium text-charcoal/60">
            Currently in effect: {effectiveValue ? 'On' : 'Off'}
          </p>
          {lockedNote && (
            <p className="mt-1 text-xs italic text-gray-400">{lockedNote}</p>
          )}
        </div>
      )}
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={meta.label}
        onKeyDown={handleKeyDown}
        className="inline-flex flex-shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-0.5"
      >
        {options.map((opt) => {
          const selected = value === opt.val
          return (
            <button
              key={String(opt.val)}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(opt.val)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-lavender ${
                selected
                  ? 'bg-deep-purple text-white shadow-sm'
                  : 'text-charcoal hover:bg-gray-100'
              } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              {opt.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Each Payment & Delivery requirement (PORTAL-202) keyed by the hide flag of the
// field it governs, so it renders as a sub-row under that field's Hide row.
const REQUIRED_META_BY_HIDE_KEY = new Map(
  PAYMENT_DELIVERY_REQUIRED_SETTINGS.map((m) => [REQUIRED_FIELD_HIDE_KEY[m.key], m]),
)

/* --------------------------------------------------------------------------
   Page
   -------------------------------------------------------------------------- */

export function ProviderSettings() {
  const { pharmacy } = useAdminAuth()
  const { providerId } = useParams<{ providerId: string }>()
  const navigate = useNavigate()
  const { success: showSuccess, error: showError } = useToast()

  const slug = pharmacy?.portal_slug

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [provider, setProvider] = useState<ProviderResponse | null>(null)
  const [defaults, setDefaults] = useState<ProviderSettingsValues | null>(null)
  // Resolved effective values from the backend — reflects the parent→child
  // cascade so the admin can see what actually applies; refreshed on each
  // auto-saved change (PORTAL-72).
  const [effective, setEffective] = useState<ProviderSettingsValues | null>(null)
  const [overrides, setOverrides] = useState<OverrideState | null>(null)
  // Per-provider veterinarian designation (PORTAL-78). A plain boolean (not a
  // tri-state override) shown only when NPI is not required for this provider.
  const [isVeterinarian, setIsVeterinarian] = useState(false)

  // Override Settings sits under the provider's details page (PORTAL-74).
  const providerDetailsPath =
    slug && providerId ? `/rx/${slug}/admin/providers/${providerId}` : ''

  const load = useCallback(async () => {
    if (!slug || !providerId) {
      // Nothing to load yet (admin pharmacy/route not resolved) — clear the
      // initial spinner so the page doesn't hang on "Loading…".
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [providerRes, settingsRes] = await Promise.all([
        getProvider(slug, providerId),
        getProviderSettings(slug, providerId),
      ])
      setProvider(providerRes)
      setDefaults(settingsRes.defaults)
      setEffective(settingsRes.effective)
      setOverrides(overridesFromResponse(settingsRes))
      setIsVeterinarian(settingsRes.is_veterinarian)
    } catch (err) {
      setError(
        err instanceof ApiClientError
          ? err.getUserMessage()
          : 'Failed to load provider settings.'
      )
    } finally {
      setLoading(false)
    }
  }, [slug, providerId])

  useEffect(() => {
    load()
  }, [load])

  function setOverride(key: SettingKey, value: boolean | null) {
    setOverrides((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  // Auto-save a single override the moment its control changes (PORTAL-72),
  // mirroring the optimistic pattern the pharmacy Settings page uses. Every save
  // disables all rows (via `saving`) so changes serialize: each PUT returns the
  // full recomputed effective/cascade state, and serializing avoids a stale
  // full-state overwrite racing the parent→child cascade.
  async function handleOverrideChange(meta: SettingMeta, value: boolean | null) {
    if (!slug || !providerId || !overrides) return
    // No-op click on the already-selected option — skip the PUT (and its audit).
    if (overrides[meta.key] === value) return
    const prev = overrides[meta.key]
    setOverride(meta.key, value) // optimistic
    setSaving(true)
    try {
      const payload: ProviderSettingsUpdate = {}
      payload[`${meta.key}_override` as keyof ProviderSettingsUpdate] = value
      const updated = await updateProviderSettings(slug, providerId, payload)
      setDefaults(updated.defaults)
      setEffective(updated.effective)
      setOverrides(overridesFromResponse(updated))
      setIsVeterinarian(updated.is_veterinarian)
      showSuccess(`${meta.label} updated`)
    } catch (err) {
      setOverride(meta.key, prev) // revert
      showError(
        err instanceof ApiClientError
          ? err.getUserMessage()
          : 'Failed to update setting'
      )
    } finally {
      setSaving(false)
    }
  }

  // Auto-save the veterinarian designation (PORTAL-78). Same optimistic pattern
  // as the tri-state rows; the PUT returns the full recomputed state. Turning the
  // flag on forces NPI/DEA off on the backend, and turning it off clears those
  // overrides back to the portal default — writing back `updated.effective` and
  // `updated.overrides` here makes those rows update immediately either way.
  async function handleVeterinarianChange(value: boolean) {
    if (!slug || !providerId) return
    const prev = isVeterinarian
    setIsVeterinarian(value) // optimistic
    setSaving(true)
    try {
      const updated = await updateProviderSettings(slug, providerId, {
        is_veterinarian: value,
      })
      setDefaults(updated.defaults)
      setEffective(updated.effective)
      setOverrides(overridesFromResponse(updated))
      setIsVeterinarian(updated.is_veterinarian)
      showSuccess('Veterinarian updated')
    } catch (err) {
      setIsVeterinarian(prev) // revert
      showError(
        err instanceof ApiClientError
          ? err.getUserMessage()
          : 'Failed to update setting'
      )
    } finally {
      setSaving(false)
    }
  }

  // The section is hidden for this provider when its OWN resolved parent value
  // hides it: the explicit override wins, otherwise the inherited pharmacy
  // global. Deliberately NOT `effective.hide_payment_delivery_section` — that
  // also flips On when all three children are hidden individually, which would
  // trap the admin by locking the very children they're editing.
  const sectionHiddenByParent =
    overrides?.hide_payment_delivery_section ??
    defaults?.hide_payment_delivery_section ??
    false

  const renderRow = (meta: SettingMeta, compactLabel?: string) => {
    // A requirement (PORTAL-202) is locked while the field it governs is
    // effectively hidden for this provider — it can't be filled, so the
    // backend resolves the requirement to Off.
    const hideKey = REQUIRED_FIELD_HIDE_KEY[meta.key]
    const fieldHidden = !!hideKey && !!effective?.[hideKey]
    const locked =
      (sectionHiddenByParent && PAYMENT_DELIVERY_CHILD_KEYS.includes(meta.key)) ||
      fieldHidden
    return (
      <div key={meta.key} className={locked ? 'opacity-60' : ''}>
        <SettingRow
          meta={meta}
          value={overrides ? overrides[meta.key] : null}
          defaultValue={defaults ? defaults[meta.key] : false}
          effectiveValue={effective ? effective[meta.key] : false}
          onChange={(value) => handleOverrideChange(meta, value)}
          disabled={saving || locked}
          lockedNote={
            fieldHidden
              ? 'Locked while the field is hidden'
              : locked
                ? 'Locked while the section is hidden'
                : undefined
          }
          compactLabel={compactLabel}
        />
      </div>
    )
  }

  // A Payment & Delivery field's requirement renders as a compact sub-row
  // under its Hide row; every other setting is a single row.
  const renderRows = (metas: SettingMeta[]) =>
    metas.map((meta, idx) => {
      const requiredMeta = REQUIRED_META_BY_HIDE_KEY.get(meta.key)
      return (
        <div key={meta.key} className={idx > 0 ? 'border-t border-gray-100' : ''}>
          {renderRow(meta)}
          {requiredMeta && renderRow(requiredMeta, 'Required to submit')}
        </div>
      )
    })

  return (
    <div className="p-6 sm:p-8">
      <div className="max-w-4xl">
        {/* Back link */}
        <button
          type="button"
          onClick={() => navigate(providerDetailsPath)}
          className="mb-4 inline-flex items-center text-sm text-charcoal/70 hover:text-charcoal"
        >
          <ArrowLeft className="mr-1.5 h-4 w-4" />
          Back to Provider Details
        </button>

        {/* Header */}
        <div className="mb-6">
          <h1 className="font-serif text-2xl text-charcoal sm:text-3xl">
            Override Settings
          </h1>
          <p className="mt-1 text-charcoal/70">
            {provider?.name || provider?.email
              ? `Configure settings for ${provider.name || provider.email}. `
              : 'Configure settings for this provider. '}
            Each setting inherits the portal default unless you override it here.
            Changes are saved automatically.
          </p>
        </div>

        {loading ? (
          <Card>
            <p className="py-8 text-center text-sm text-gray-500">Loading settings…</p>
          </Card>
        ) : error ? (
          <Card>
            <div className="py-8 text-center">
              <p className="text-sm text-error">{error}</p>
              <Button variant="secondary" className="mt-4" onClick={load}>
                Retry
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-6">
            <Card header="Provider Requirements">
              <div className="divide-y divide-gray-100">
                {renderRows(
                  // Canadian pharmacies have no NPI / DEA (PORTAL-161)
                  generalSettingsFor(pharmacy?.country)
                )}
              </div>
              {/* Veterinarian designation (PORTAL-78) — always editable. Turning
                  it on drives this provider's NPI Required and DEA Required to Off
                  (a vet has no NPI/DEA); turning it off restores them to the portal
                  default. The backend returns the recomputed state so those rows
                  update immediately. */}
              <div className="border-t border-gray-100 py-4">
                <Checkbox
                  label="Veterinarian"
                  checked={isVeterinarian}
                  disabled={saving}
                  onChange={(e) => handleVeterinarianChange(e.target.checked)}
                />
                <p className="mt-0.5 text-xs text-gray-500">
                  Mark this provider as a veterinarian. Turning this on sets their
                  NPI and DEA requirements to Off; turning it off restores them to
                  the portal default.
                </p>
              </div>
            </Card>

            <Card header="Payment & Delivery">
              <p className="pb-2 text-xs text-gray-500">
                “Hide Payment &amp; Delivery Section” hides the whole section. The
                individual fields below are also hidden when the section is hidden.
              </p>
              {/* Parent (section) row */}
              <div className="divide-y divide-gray-100">
                {renderRows(PAYMENT_DELIVERY_SETTINGS.slice(0, 1))}
              </div>
              {/* Child fields — indented under the section (left rule) to show
                  they belong to it, and greyed/locked while it is hidden. Each
                  carries its "Required to submit" sub-row (PORTAL-202), locked
                  while the field is hidden. */}
              <div className="ml-1 mt-1 border-l-2 border-gray-200 pl-4 sm:pl-5">
                <div className="divide-y divide-gray-100">
                  {renderRows(PAYMENT_DELIVERY_SETTINGS.slice(1))}
                </div>
              </div>
            </Card>

          </div>
        )}
      </div>
    </div>
  )
}

export default ProviderSettings
